/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';
import BolInvoiceRequest from '../../../../models/bolInvoiceRequestModel';
import Invoice, {InvoiceFields} from '../../../../models/invoiceModel';
import {trackPlatformAction} from '../../../../util/analytics';
import {ActivateHandle, Country, InvoiceDataErrors} from '../../../../util/interfaces';
import {getCountryLabel} from '../../../../util/countryUtil';
import {createErrorMessage} from '../../../../util/utils';
import {ResponseData} from '@dvrd/dvr-controls';
import InvoiceData from './invoiceData';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: (invoiceRequest: BolInvoiceRequest, invoice: Invoice, upload: boolean) => void;
}

const emptyErrors: InvoiceDataErrors = {
    firstname: null,
    lastname: null,
    address: null,
    houseNumber: null,
    zipCode: null,
    city: null,
    invoiceNumber: null,
    invoiceNumberPrefix: null
};

function InvoiceDataController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSuccess} = props;
    const {t} = useTranslation();
    const [invoice, setInvoice] = useState<Invoice>(new Invoice());
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<InvoiceDataErrors>(emptyErrors);
    const [active, setActive] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceNumberPrefix, setInvoiceNumberPrefix] = useState('');
    const invoiceRequest = useRef<BolInvoiceRequest>(new BolInvoiceRequest());

    function onActivate(_invoiceRequest: BolInvoiceRequest, _invoice: Invoice) {
        _invoice.firstname = _invoiceRequest.firstname;
        _invoice.lastname = _invoiceRequest.lastname;
        _invoice.street = _invoiceRequest.address;
        _invoice.houseNumber = _invoiceRequest.houseNumber;
        _invoice.houseNumberSuffix = _invoiceRequest.houseNumberExtension;
        _invoice.zipCode = _invoiceRequest.zipCode;
        _invoice.city = _invoiceRequest.city;
        _invoice.country = getCountryLabel(_invoiceRequest.countryCode) as Country;
        _invoice.company = _invoiceRequest.companyName;
        _invoice.vatNumber = _invoiceRequest.vatNumber;
        _invoice.cocNumber = _invoiceRequest.kvkNumber;
        invoiceRequest.current = _invoiceRequest;
        setInvoice(_invoice);
        setActive(true);
    }

    function onChange(key: keyof InvoiceFields) {
        return function (value: string) {
            setInvoice(invoice.clone().setField(key, value));
        };
    }

    function onChangeInvoiceNumber(value: string) {
        setInvoiceNumber(value);
    }

    function onChangeInvoiceNumberPrefix(value: string) {
        setInvoiceNumberPrefix(value);
    }

    function onResetError(key: keyof InvoiceDataErrors) {
        return function () {
            setErrors({...errors, [key]: null});
        };
    }

    function onClose() {
        setActive(false);
        setInvoice(new Invoice());
        setErrors(emptyErrors);
        setLoading(false);
        invoiceRequest.current = new BolInvoiceRequest();
    }

    function onSubmit(upload: boolean) {
        return function () {
            if (!validateValues()) return;
            setLoading(true);
            invoice
                .clone()
                .assignInvoiceNum(
                    invoiceNumberPrefix,
                    invoiceNumber,
                    (_: Invoice, success: boolean, data: ResponseData) => {
                        if (!success) {
                            setLoading(false);
                            return showDialog(
                                createErrorMessage(
                                    data.message ??
                                        t(
                                            'invoice.request.data.setNumFailed',
                                            'Het instellen van het factuurnummer is niet gelukt.'
                                        )
                                ),
                                t('invoice.request.data.setNumFailedTitle', 'Factuurnummer instellen mislukt')
                            );
                        }
                        invoice.update((invoice: Invoice, success: boolean, data: ResponseData) => {
                            setLoading(false);
                            if (success) {
                                trackPlatformAction('invoices.requests', 'create_request');
                                onSuccess(invoiceRequest.current, invoice, upload);
                                onClose();
                                if (!upload)
                                    showSnackbar(
                                        t('invoice.request.data.savedSuccess', 'De factuurgegevens zijn opgeslagen')
                                    );
                            } else
                                showDialog(
                                    createErrorMessage(
                                        data.message ??
                                            t(
                                                'invoice.request.data.saveFailed',
                                                'Het opslaan van de factuurgegevens is mislukt.'
                                            )
                                    ),
                                    t('invoice.request.data.saveFailedTitle', 'Opslaan mislukt')
                                );
                        });
                    }
                );
        };
    }

    function validateValues(): boolean {
        let valid = true;
        const _errors = {...errors};
        if (!invoice.firstname) {
            valid = false;
            _errors.firstname = t('invoice.request.data.errFirstName', 'Vul de voornaam in');
        }
        if (!invoice.lastname) {
            valid = false;
            _errors.lastname = t('invoice.request.data.errLastName', 'Vul de achternaam in');
        }
        if (!invoice.street) {
            valid = false;
            _errors.address = t('invoice.request.data.errAddress', 'Vul het adres in');
        }
        if (!invoice.houseNumber) {
            valid = false;
            _errors.houseNumber = t('invoice.request.data.errHouseNumber', 'Vul het huisnummer in');
        }
        if (!invoice.zipCode) {
            valid = false;
            _errors.zipCode = t('invoice.request.data.errZipCode', 'Vul de postcode in');
        }
        if (!invoice.city) {
            valid = false;
            _errors.city = t('invoice.request.data.errCity', 'Vul de woonplaats in');
        }
        if (!invoiceNumber) {
            valid = false;
            _errors.invoiceNumber = t('invoice.request.data.errInvoiceNumber', 'Vul het factuurnummer in');
        }
        if (!invoiceNumberPrefix) {
            valid = false;
            _errors.invoiceNumberPrefix = t('invoice.request.data.errPrefix', 'Vul het voorvoegsel in');
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <InvoiceData
            onClose={onClose}
            onChange={onChange}
            onChangeInvoiceNumber={onChangeInvoiceNumber}
            onChangeInvoiceNumberPrefix={onChangeInvoiceNumberPrefix}
            onResetError={onResetError}
            onSubmit={onSubmit}
            invoice={invoice}
            loading={loading}
            errors={errors}
            active={active}
            invoiceNumber={invoiceNumber}
            invoiceNumberPrefix={invoiceNumberPrefix}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(InvoiceDataController);
